// evidence.js — "answer plus the word that proves it".
//
// Two commitments, checked together: the option and the word in the sentence
// that decides it. Neither half can be probed on its own, so the coin-flip
// answer stops being worth a guess — the price of the guess is the evidence
// click, which is where the rule lives. On a wrong first attempt the student
// is told which half failed (and the author's hint, if there is one) but never
// the right answer.
//
// Markup comes from the evidence.lua filter.

const READY = 'data-ph-ev-ready';

// atob yields a byte string; the payload is UTF-8. Decoding it this way keeps
// the widget working where TextDecoder is absent (older WebViews, jsdom).
function utf8FromBinary(bin) {
    let escaped = '';
    for (let i = 0; i < bin.length; i += 1) {
        escaped += '%' + bin.charCodeAt(i).toString(16).padStart(2, '0');
    }
    try {
        return decodeURIComponent(escaped);
    } catch (_) {
        return bin;
    }
}

function decodeSolution(root) {
    try {
        return JSON.parse(utf8FromBinary(atob(root.dataset.solution || '')));
    } catch (err) {
        console.error('[evidence] undecodable solution', err);
        return { opt: -1, keys: [] };
    }
}

class Evidence {
    constructor(root) {
        this.root = root;
        this.solution = decodeSolution(root);
        this.maxAttempts = parseInt(root.dataset.attempts || '2', 10) || 2;
        this.hint = root.dataset.hint || '';

        this.tokens = Array.from(root.querySelectorAll('.ph-ev-tok'));
        this.options = Array.from(root.querySelectorAll('.ph-ev-opt'));
        this.feedback = root.querySelector('.ph-ev-feedback');

        this.choice = null;
        this.evidence = null;
        this.attempts = 0;
        this.done = false;

        this.tokens.forEach((tok) => {
            tok.addEventListener('click', () => this.pickEvidence(Number(tok.dataset.i)));
            tok.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.pickEvidence(Number(tok.dataset.i));
                }
            });
        });
        this.options.forEach((opt) => {
            opt.addEventListener('click', () => this.pickOption(Number(opt.dataset.i)));
        });

        this.buildActions();
        this.render();

        if (root.dataset.reveal === '1') this.reveal(true);
    }

    buildActions() {
        this.actions = document.createElement('div');
        this.actions.className = 'ph-ev-actions';

        this.checkBtn = document.createElement('button');
        this.checkBtn.type = 'button';
        this.checkBtn.className = 'ph-ev-btn';
        this.checkBtn.textContent = 'Prüfen';
        this.checkBtn.disabled = true;
        this.checkBtn.addEventListener('click', () => this.check());
        this.actions.appendChild(this.checkBtn);

        this.note = document.createElement('span');
        this.note.className = 'ph-ev-note';
        this.note.textContent = 'Antwort wählen und das entscheidende Wort anklicken.';
        this.actions.appendChild(this.note);

        this.root.insertBefore(this.actions, this.feedback);
    }

    pickOption(i) {
        if (this.done) return;
        this.choice = i;
        this.render();
    }

    pickEvidence(i) {
        if (this.done) return;
        this.evidence = this.evidence === i ? null : i;
        this.render();
    }

    render() {
        this.options.forEach((opt, i) => {
            opt.classList.toggle('ph-ev-chosen', this.choice === i);
        });
        this.tokens.forEach((tok, i) => {
            tok.classList.toggle('ph-ev-chosen', this.evidence === i);
        });
        const ready = this.choice !== null && this.evidence !== null;
        this.checkBtn.disabled = !ready || this.done;
        if (!this.done) {
            this.note.textContent = ready
                ? ''
                : 'Antwort wählen und das entscheidende Wort anklicken.';
        }
    }

    say(text, tone) {
        this.feedback.textContent = text;
        this.feedback.className = `ph-ev-feedback${tone ? ' ph-ev-' + tone : ''}`;
    }

    check() {
        if (this.done) return;
        this.attempts += 1;

        const optionOk = this.choice === this.solution.opt;
        const evidenceOk = (this.solution.keys || []).includes(this.evidence);

        if (optionOk && evidenceOk) {
            this.done = true;
            this.options[this.choice].classList.add('ph-ev-ok');
            this.tokens[this.evidence].classList.add('ph-ev-ok');
            this.say(this.attempts === 1 ? 'Stimmt — Antwort und Beleg.' : 'Jetzt stimmt beides.', 'ok');
            this.finish();
            return;
        }

        if (this.attempts >= this.maxAttempts) {
            this.say('Auch das stimmt noch nicht.', 'bad');
            this.checkBtn.remove();
            const solve = document.createElement('button');
            solve.type = 'button';
            solve.className = 'ph-ev-btn ph-ev-btn-quiet';
            solve.textContent = 'Lösung zeigen';
            solve.addEventListener('click', () => this.reveal());
            this.actions.appendChild(solve);
            return;
        }

        // Which half failed is useful; what the right half would be is not.
        let msg;
        if (optionOk) {
            msg = 'Die Antwort stimmt — aber das ist nicht das Wort, das darüber entscheidet.';
        } else if (evidenceOk) {
            msg = 'Du siehst auf das richtige Wort — lies die Antworten noch einmal.';
        } else {
            msg = 'Beides noch nicht. Suche zuerst das Wort, an dem es hängt.';
        }
        if (this.hint) msg += ` ${this.hint}`;
        this.say(msg, 'warn');
        const left = this.maxAttempts - this.attempts;
        this.note.textContent = left === 1 ? 'noch 1 Versuch' : `noch ${left} Versuche`;
    }

    reveal(silent) {
        this.done = true;
        this.options.forEach((opt, i) => {
            opt.classList.toggle('ph-ev-ok', i === this.solution.opt);
            opt.classList.remove('ph-ev-chosen');
        });
        this.tokens.forEach((tok, i) => {
            tok.classList.toggle('ph-ev-ok', (this.solution.keys || []).includes(i));
            tok.classList.remove('ph-ev-chosen');
        });
        if (!silent) this.say('Lösung.', 'solution');
        this.finish();
    }

    finish() {
        this.actions.innerHTML = '';
        this.options.forEach((opt) => { opt.disabled = true; });
        this.tokens.forEach((tok) => { tok.classList.add('ph-ev-locked'); });
    }
}

export function initEvidence() {
    document.querySelectorAll('[data-ph-ev]').forEach((root) => {
        if (root.hasAttribute(READY)) return;
        root.setAttribute(READY, 'true');
        try {
            new Evidence(root);
        } catch (err) {
            console.error('[evidence] init failed', err);
        }
    });
}
